import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import LandingVideo from '../components/Landing/LandingVideo';
import { API_URL } from '../config';
import {
  FREE_PLAN_PRICE,
  FLEET_PRICE_ADDON,
  ONBOARDING_PLAN_CARDS,
  OWNER_OPERATOR_VEHICLE_LIMIT,
  PAYWALL_PLANS,
  getVehicleLimit,
} from '../config/pricingPublic';
import './landing-v2.css';

const SIGNUP = '/register';
const SIGNIN = '/login';
const DEFAULT_DAY_COST = 1500;

function WarnIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E09B1A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l10 18H2L12 3z" />
      <path d="M12 10v5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7FD6A4" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function QuoteMark() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="#E09B1A" aria-hidden="true">
      <path d="M4 18v-5c0-4 2-6.5 6-7l.5 2C8.5 8.5 8 10 8 11h3v7H4zm10 0v-5c0-4 2-6.5 6-7l.5 2c-2 .5-2.5 2-2.5 3h3v7h-7z" />
    </svg>
  );
}

function money(n) {
  return `$${Number(n).toLocaleString('en-US')}`;
}

function dollarsFromPrice(price) {
  const n = Number(String(price).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export default function LandingV2() {
  const freePlan = ONBOARDING_PLAN_CARDS.find((p) => p.tier === 'free');
  const ooPlan = PAYWALL_PLANS.find((p) => p.tier === 'owner-operator');
  const fleetPlan = PAYWALL_PLANS.find((p) => p.tier === 'fleet');
  const ooMonthly = dollarsFromPrice(ooPlan?.price);
  const fleetLimit = getVehicleLimit('fleet');
  const ooTruckLimit = OWNER_OPERATOR_VEHICLE_LIMIT;

  const [dayCost, setDayCost] = useState(String(DEFAULT_DAY_COST));
  const [waitEmail, setWaitEmail] = useState('');
  const [waitSize, setWaitSize] = useState('Just me');
  const [waitNeed, setWaitNeed] = useState('');
  const [waitStatus, setWaitStatus] = useState(null);
  const [waitSending, setWaitSending] = useState(false);

  const dailyNumber = useMemo(() => {
    const n = Number(String(dayCost).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }, [dayCost]);

  const monthsCovered = ooMonthly > 0 ? Math.floor(dailyNumber / ooMonthly) : 0;

  const onDayCost = (e) => {
    const next = e.target.value.replace(/[^0-9]/g, '');
    setDayCost(next);
  };

  const onWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (waitSending) return;
    setWaitStatus(null);
    setWaitSending(true);
    try {
      await axios.post(`${API_URL}/api/waitlist/drug-testing`, {
        email: waitEmail,
        driverCount: waitSize,
        interest: waitNeed || undefined,
      });
      setWaitStatus('success');
    } catch {
      setWaitStatus('error');
    } finally {
      setWaitSending(false);
    }
  };

  return (
    <div className="landing-v2">
      <header className="lv2-nav">
        <div className="lv2-wrap lv2-nav__inner">
          <div className="lv2-brand">
            <div className="lv2-brand__name">Sovrynn</div>
            <div className="lv2-brand__mark" aria-hidden="true">
              <span />
              <span />
            </div>
          </div>
          <div className="lv2-nav__links">
            <Link to={SIGNIN} className="lv2-nav__signin">Sign In</Link>
            <Link to={SIGNUP} className="lv2-nav__cta">Get Started Free</Link>
          </div>
        </div>
      </header>

      <section className="lv2-hero">
        <div className="lv2-wrap lv2-hero__inner">
          <div className="lv2-hero__copy">
            <div className="lv2-kicker">For owner-operators and small fleets</div>
            <h1 className="lv2-display lv2-display--xl">
              DOT, <span className="lv2-accent">handled.</span>
            </h1>
            <p className="lv2-lede" style={{ maxWidth: 520 }}>
              Sovrynn watches your deadlines, keeps your driver and truck files in order, and tells you exactly what to do next. You drive.
            </p>
            <div>
              <Link to={SIGNUP} className="lv2-btn lv2-btn--primary lv2-btn--lg">
                See where you stand, free
              </Link>
            </div>
            <div className="lv2-hero__note">
              Free to start, no credit card. Your DRI Score™, a 0 to 100 compliance grade, in under 5 minutes.
            </div>
          </div>
          <div className="lv2-hero__phone-col">
            <div className="lv2-phone">
              <div className="lv2-phone__screen">
                <LandingVideo
                  src="/videos/landing/hero_sms"
                  width={580}
                  height={712}
                  ariaLabel="Rynn texting a medical card expiration reminder"
                />
              </div>
            </div>
            <p className="lv2-caption">Rynn by text — coming soon</p>
          </div>
        </div>
      </section>

      <section className="lv2-parked">
        <div className="lv2-wrap lv2-parked__inner">
          <div className="lv2-parked__copy">
            <div className="lv2-kicker">What it really costs</div>
            <h2 className="lv2-display lv2-display--lg">
              Parked trucks <span className="lv2-accent">don&apos;t get paid.</span>
            </h2>
            <p className="lv2-body">
              An out-of-service order keeps your truck off the road until the problem is fixed. For one Kansas City owner-operator, a single day cost $1,500.
            </p>
            <div className="lv2-callouts">
              <div className="lv2-callout">
                <WarnIcon />
                <span>Driving without a valid medical certificate gets a driver placed out of service.</span>
              </div>
              <div className="lv2-callout">
                <WarnIcon />
                <span>Sovrynn can&apos;t fix a bad brake. It keeps your paperwork from parking you.</span>
              </div>
            </div>
          </div>
          <div className="lv2-calc">
            <label htmlFor="park-day">What does one day parked cost you?</label>
            <div className="lv2-calc__field">
              <span>$</span>
              <input
                id="park-day"
                type="text"
                inputMode="numeric"
                value={dayCost}
                onChange={onDayCost}
              />
              <span className="lv2-calc__suffix">per day</span>
            </div>
            <div className="lv2-calc__result">
              <div className="lv2-fine">
                One parked day costs {money(dailyNumber)}. That pays for
              </div>
              <div className="lv2-calc__months">
                {monthsCovered.toLocaleString('en-US')}
                <span> months of Sovrynn</span>
              </div>
              <div className="lv2-calc__hint">
                {ooPlan?.title} plan, {ooPlan?.price} a month. Starting number is one real operator&apos;s day. Enter yours.
              </div>
            </div>
            <Link to={SIGNUP} className="lv2-btn lv2-btn--primary lv2-btn--block">
              See where you stand, free
            </Link>
          </div>
        </div>
      </section>

      <section className="lv2-handled" id="handled">
        <div className="lv2-wrap">
          <div className="lv2-handled__head">
            <div className="lv2-kicker">What gets handled</div>
            <h2 className="lv2-display lv2-display--md">The paperwork you keep meaning to get to.</h2>
          </div>
          <div className="lv2-cards">
            <article className="lv2-card">
              <div className="lv2-card__clip">
                <LandingVideo
                  src="/videos/landing/f1_expiry"
                  width={588}
                  height={304}
                  ariaLabel="Expiration alert example"
                />
              </div>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E09B1A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <h3>Never get parked for an expired card</h3>
              <p>Medical card, CDL, MVR and your other driver documents. Sovrynn tracks every date and warns you before it becomes a problem.</p>
            </article>
            <article className="lv2-card">
              <div className="lv2-card__clip">
                <LandingVideo
                  src="/videos/landing/f2_dot"
                  width={588}
                  height={304}
                  ariaLabel="DOT number lookup example"
                />
              </div>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E09B1A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3l8 3v6c0 4.5-3.2 8-8 9-4.8-1-8-4.5-8-9V6l8-3z" />
                <path d="M8.5 12l2.5 2.5L15.5 10" />
              </svg>
              <h3>No surprises at inspection</h3>
              <p>Your authority and vehicle status, checked against FMCSA records, so a change doesn&apos;t surprise you at a roadside inspection.</p>
            </article>
            <article className="lv2-card">
              <div className="lv2-card__clip">
                <LandingVideo
                  src="/videos/landing/f3_ifta"
                  width={588}
                  height={304}
                  ariaLabel="IFTA mileage and fuel report example"
                />
              </div>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E09B1A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="4" y="3" width="16" height="18" rx="2" />
                <path d="M8 8h8" />
                <path d="M8 12h8" />
                <path d="M8 16h5" />
              </svg>
              <h3>IFTA, ready to file</h3>
              <p>Log your miles and fuel, and Sovrynn builds your IFTA report with the calculations done. You file it yourself, ahead of the quarterly dates: Apr 30, Jul 31, Oct 31 and Jan 31.</p>
            </article>
          </div>
          <p className="lv2-caption lv2-handled__caption">Illustrative example data</p>
        </div>
      </section>

      <section className="lv2-band lv2-text">
        <div className="lv2-wrap lv2-text__inner">
          <div className="lv2-text__copy">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <div className="lv2-kicker">No app to open</div>
              <div className="lv2-wait__chip">Coming soon</div>
            </div>
            <h2 className="lv2-display lv2-display--md">Rynn, by text.</h2>
            <p className="lv2-body">
              Rynn texts you before a deadline hits. Soon you&apos;ll be able to text back a photo of your new card, reply Y to confirm, and your file updates.
            </p>
            <Link to={SIGNUP} className="lv2-btn lv2-btn--ghost" style={{ alignSelf: 'flex-start' }}>
              Get started free
            </Link>
          </div>
          <div className="lv2-text__phones">
            <div className="lv2-sms">
              <div className="lv2-bubble lv2-bubble--in">Your medical card expires in 27 days. Book your DOT physical, then text me a photo of the new one.</div>
              <div className="lv2-sms-card">
                <strong>Medical card</strong>
                <span>27 days left</span>
              </div>
              <div className="lv2-bubble lv2-bubble--out">Booked for Thursday</div>
            </div>
            <div className="lv2-sms">
              <div className="lv2-sms-photo">Photo of medical card</div>
              <div className="lv2-bubble lv2-bubble--in">Got it. Expires 03/14/2028. Is that right? Reply Y to save.</div>
              <div className="lv2-bubble lv2-bubble--out">Y</div>
            </div>
          </div>
        </div>
      </section>

      <section className="lv2-quote">
        <div className="lv2-wrap lv2-quote__inner">
          <figure>
            <QuoteMark />
            <blockquote>
              Sovrynn has given me visibility I didn&apos;t have before. I now run my operation worry free knowing exactly where I stand.
            </blockquote>
            <figcaption>
              <img
                src="/images/janese.jpg"
                alt="Janese S., owner of Stevenson's Trucking LLC"
                width={56}
                height={56}
              />
              <div>
                <strong>Janese S.</strong>
                <span>Stevenson&apos;s Trucking LLC · Kansas City, MO</span>
              </div>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="lv2-dri">
        <div className="lv2-wrap lv2-dri__inner">
          <div className="lv2-dri__copy">
            <div className="lv2-kicker">Know where you stand</div>
            <h2 className="lv2-display lv2-display--md">One number. One link. Proof on demand.</h2>
            <p className="lv2-body">
              Your DRI Score™ shows your compliance standing from 0 to 100. Your Compliance Passport™ turns it into a verified record you can send to a broker, a shipper or an auditor.
            </p>
            <div className="lv2-checks">
              <div><CheckIcon /><span>Scored from your documents, vehicles and authority</span></div>
              <div><CheckIcon /><span>Public verification link and PDF download</span></div>
              <div><CheckIcon /><span>Updates automatically as your records change</span></div>
            </div>
            <Link to={SIGNUP} className="lv2-btn lv2-btn--primary" style={{ alignSelf: 'flex-start' }}>
              Get your free DRI Score™
            </Link>
          </div>
          <div className="lv2-dri__cards">
            <div className="lv2-score">
              <div>
                <div className="lv2-score__label">DRI Score™</div>
                <div className="lv2-score__num">78<span>/100</span></div>
                <div className="lv2-pill">Compliance Verified</div>
              </div>
              <div className="lv2-score__rows">
                <div><span>Document Health</span><span>78%</span></div>
                <div><span>Vehicle Readiness™</span><span>85/100</span></div>
                <div><span>Authority Health</span><span>Current</span></div>
              </div>
            </div>
            <div className="lv2-passport">
              <div className="lv2-passport__top">
                <strong>Compliance Passport™</strong>
                <span>SVN-A3K9P2</span>
              </div>
              <div className="lv2-passport__meta">Verified living compliance record · Generated today · Expires in 30 days</div>
              <div className="lv2-passport__rows">
                <div><span>Medical Examiner&apos;s Certificate</span><span className="lv2-ok">Complete</span></div>
                <div><span>Commercial Driver&apos;s License</span><span className="lv2-ok">Complete</span></div>
                <div><span>Motor Vehicle Record</span><span className="lv2-soon">Expiring soon</span></div>
              </div>
            </div>
            <p className="lv2-caption" style={{ textAlign: 'left' }}>Sample data</p>
          </div>
        </div>
      </section>

      <section className="lv2-band lv2-how">
        <div className="lv2-wrap">
          <div className="lv2-how__head">
            <div className="lv2-kicker">How it starts</div>
            <h2 className="lv2-display lv2-display--sm">Three steps. No paperwork pile.</h2>
          </div>
          <div className="lv2-cards">
            <article className="lv2-step">
              <div className="lv2-step__n">01</div>
              <h3>Add your DOT number</h3>
              <p>Sovrynn looks up your FMCSA record so you start with your real information.</p>
            </article>
            <article className="lv2-step">
              <div className="lv2-step__n">02</div>
              <h3>Add your documents</h3>
              <p>Upload your CDL, medical card and truck details. That&apos;s what your score and alerts run on.</p>
            </article>
            <article className="lv2-step">
              <div className="lv2-step__n">03</div>
              <h3>Get warned, not surprised</h3>
              <p>Sovrynn alerts you before dates hit, and Rynn answers your questions any time.</p>
            </article>
          </div>
          <div className="lv2-how__cta">
            <Link to={SIGNUP} className="lv2-btn lv2-btn--primary">See where you stand, free</Link>
            <div className="lv2-fine">Free to start. No credit card required.</div>
          </div>
        </div>
      </section>

      <section className="lv2-pricing" id="pricing">
        <div className="lv2-wrap">
          <div className="lv2-pricing__head">
            <div className="lv2-kicker">Pricing</div>
            <h2 className="lv2-display lv2-display--md">Start free. Upgrade when it pays for itself.</h2>
            <p className="lv2-fine">The free plan needs no credit card, and you can upgrade any time.</p>
          </div>
          <div className="lv2-cards">
            <article className="lv2-plan">
              <div className="lv2-plan__name">{freePlan?.title}</div>
              <div className="lv2-plan__price">{FREE_PLAN_PRICE}</div>
              <div className="lv2-plan__blurb">{freePlan?.blurb}</div>
              <div className="lv2-plan__feats">
                <div>Compliance dashboard</div>
                <div>Document storage</div>
                <div>Vehicle tracking (1 to {ooTruckLimit} trucks)</div>
                <div>Authority lookup</div>
                <div>Basic alerts</div>
              </div>
              <Link to={SIGNUP} className="lv2-btn lv2-btn--ghost">Get started free</Link>
            </article>
            <article className="lv2-plan lv2-plan--featured">
              <div className="lv2-plan__top">
                <div className="lv2-plan__name">{ooPlan?.title}</div>
                <div className="lv2-plan__badge">MOST POPULAR</div>
              </div>
              <div className="lv2-plan__price">
                {ooPlan?.price}<span>/{ooPlan?.period}</span>
              </div>
              <div className="lv2-plan__blurb">You&apos;re the driver, dispatcher and safety manager. Sovrynn covers the last one.</div>
              <div className="lv2-plan__feats">
                <div>Compliance dashboard</div>
                <div>DRI Score™</div>
                <div>Compliance Passport™</div>
                <div>Vehicle Readiness Score™</div>
                <div>Document expiration alerts</div>
                <div>IFTA mileage and fuel tracking with report</div>
                <div>Authority monitoring</div>
                <div>Rynn assistant</div>
              </div>
              <Link to={SIGNUP} className="lv2-btn lv2-btn--primary">Get {ooPlan?.title}</Link>
            </article>
            <article className="lv2-plan">
              <div className="lv2-plan__name">{fleetPlan?.title}</div>
              <div className="lv2-plan__price">
                {fleetPlan?.price}
                <span>/{fleetPlan?.period} {FLEET_PRICE_ADDON}</span>
              </div>
              <div className="lv2-plan__blurb">Everything in Owner-Operator, built for multi-driver operations.</div>
              <div className="lv2-plan__feats">
                <div>Up to {fleetLimit} vehicles</div>
                <div>Multi-driver compliance</div>
                <div>Fleet DRI Score™</div>
                <div>Fleet Compliance Passport™</div>
                <div>Analytics dashboard and reports</div>
                <div>Priority support</div>
              </div>
              <Link to={SIGNUP} className="lv2-btn lv2-btn--ghost">Get {fleetPlan?.title}</Link>
            </article>
          </div>
        </div>
      </section>

      <section className="lv2-band lv2-faq">
        <div className="lv2-wrap lv2-faq__inner">
          <h2 className="lv2-display lv2-display--sm">Straight answers.</h2>
          <div>
            <details>
              <summary>Do I need this if I only have one truck?<span aria-hidden="true">+</span></summary>
              <div>Sovrynn is built for owner-operators and fleets of 1 to {fleetLimit} trucks. The free plan covers 1 to {ooTruckLimit} vehicles.</div>
            </details>
            <details>
              <summary>Does it cost anything to start?<span aria-hidden="true">+</span></summary>
              <div>No. The free plan needs no credit card. Upgrade only when you want the full alerts, DRI Score™ and Passport.</div>
            </details>
            <details>
              <summary>What happens when my medical card is about to expire?<span aria-hidden="true">+</span></summary>
              <div>Sovrynn warns you ahead of the date, and Rynn walks you through what to do next. Upload the new card and your record updates.</div>
            </details>
            <details>
              <summary>Where does my information come from?<span aria-hidden="true">+</span></summary>
              <div>Public FMCSA records, looked up by your DOT number, plus the documents you add yourself.</div>
            </details>
            <details>
              <summary>Does Sovrynn file my paperwork for me?<span aria-hidden="true">+</span></summary>
              <div>No. Sovrynn tells you what&apos;s due, texts you before deadlines, and builds your IFTA report so you can file it yourself.</div>
            </details>
          </div>
        </div>
      </section>

      <section className="lv2-wait" id="waitlist">
        <div className="lv2-wrap">
          <div className="lv2-wait__box">
            <div className="lv2-wait__copy">
              <div className="lv2-wait__chip">Coming soon</div>
              <h2 className="lv2-display" style={{ fontSize: 'clamp(30px, 4.5vw, 44px)' }}>
                Drug and alcohol testing, next.
              </h2>
              <p className="lv2-body" style={{ fontSize: 17, lineHeight: '27px' }}>
                CDL drivers, including owner-operators, have to be in a random drug and alcohol testing program. We&apos;re building this into Sovrynn, and what you tell us decides what we build first.
              </p>
              <p className="lv2-fine" style={{ fontSize: 15, lineHeight: '24px' }}>
                Join the waitlist for early access.
              </p>
            </div>
            <form onSubmit={onWaitlistSubmit} noValidate>
              <div className="lv2-field">
                <label htmlFor="wl-email">Email</label>
                <input
                  id="wl-email"
                  type="email"
                  placeholder="you@example.com"
                  value={waitEmail}
                  onChange={(e) => setWaitEmail(e.target.value)}
                />
              </div>
              <div className="lv2-field">
                <label htmlFor="wl-size">How many drivers?</label>
                <select id="wl-size" value={waitSize} onChange={(e) => setWaitSize(e.target.value)}>
                  <option>Just me</option>
                  <option>2 to 5</option>
                  <option>6 to 25</option>
                </select>
              </div>
              <fieldset>
                <legend>What would help most?</legend>
                {[
                  'Help me join a random testing pool',
                  'I have a provider. Track it for me',
                  'Remind me about renewals and Clearinghouse queries',
                  'Not sure yet',
                ].map((label) => (
                  <label key={label} className="lv2-radio">
                    <input
                      type="radio"
                      name="wl-need"
                      checked={waitNeed === label}
                      onChange={() => setWaitNeed(label)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </fieldset>
              <button type="submit" className="lv2-btn lv2-btn--primary" disabled={waitSending}>
                Join the waitlist
              </button>
              {waitStatus === 'success' ? (
                <p className="lv2-form-msg lv2-form-msg--ok" role="status">You&apos;re on the list</p>
              ) : null}
              {waitStatus === 'error' ? (
                <p className="lv2-form-msg lv2-form-msg--err" role="alert">Something went wrong, try again</p>
              ) : null}
              <div className="lv2-fine" style={{ fontSize: 13 }}>We&apos;ll only email you about this. No spam.</div>
            </form>
          </div>
        </div>
      </section>

      <section className="lv2-final">
        <h2 className="lv2-display">
          Get DOT <span className="lv2-accent">handled.</span>
        </h2>
        <p className="lv2-lede">Know your DRI Score™ in under 5 minutes.</p>
        <Link to={SIGNUP} className="lv2-btn lv2-btn--primary lv2-btn--lg">
          See where you stand, free
        </Link>
        <div className="lv2-fine">
          Already have an account? <Link to={SIGNIN} className="lv2-accent">Sign in</Link>
        </div>
      </section>

      <footer className="lv2-footer">
        <div className="lv2-wrap lv2-footer__inner">
          <div className="lv2-footer__brand">
            <strong>Sovrynn</strong>
            <span className="lv2-fine">DOT, handled, for the independent commercial operator. A Tearratopia Holdings Company.</span>
          </div>
          <div className="lv2-footer__links">
            <Link to="/blog">Blog</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to={SIGNIN}>Sign In</Link>
          </div>
          <div className="lv2-footer__legal">© 2026 Sovrynn LLC. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
